import DFButton from "app/widgets/DFButton/DFButton";
import "common/bindings/action";
import "app/css/widgets/MenuButton.less";
import locale from "common/managers/locale";

function _origin(direction) {
    if(direction === "left" || direction === "top") {
        return "top left";
    } else if(direction === "right") {
        return "top right";
    }
    return "bottom left";
}

function _position(direction) {
    if(direction === "left") {
        return "top right";
    } else if(direction === "top") {
        return "bottom left";
    }
    return "top left";
}

const MenuButton = DFButton.extend({

    popup: undefined,

    options: {
        name: "MenuButton",
        itemTemplate: "<li>#:data#</li>",
        direction: "bottom",
        autoBind: true,
        openWhenBusy: false,
        dataSource: [],
        // locale key of the menu context, this ends up setting the button element title
        tooltipContextKey: null
    },

    events: [
        "dataBinding",
        "dataBound",
        "select"
    ],

    init: function(element) {
        if(!$(element).attr("id")) {
            throw new Error("MenuButton widget requires an id");
        }
        DFButton.fn.init.apply(this, arguments);
        this.refresh = $.proxy(this.refresh, this);
        this._renderItem = kendo.template(this.options.itemTemplate);
        this._button();
        this._popup();
        this.setDataSource(this.options.dataSource);
    },

    destroy() {
        if(this.popup) {
            const el = this.popup.element;
            this.popup.destroy();
            el.remove();
            delete this.popup;
        }
    },

    /**
     * Override
     * Tooltip needs to present menu context since the menu icon is the same for all button menus
     */
    _tooltip() {
        let title;
        if(this.options.tooltipContextKey) {
            title = locale.get("menu.contextual", locale.get(this.options.tooltipContextKey));
        } else {
            title = locale.get("menu");
        }
        this.element.attr("title", title);
    },

    _button() {
        this.element
            .attr("aria-haspopup", "true")
            .attr("aria-controls", `${this.element.attr("id")}_menu`);
        this.element.append($("<span></span>", {
            "class": "iconMoon",
            "aria-hidden": "true",
            text: "6"
        }));
        this.bind("click", () => {
            this.popup.toggle();
        });
    },

    _popup() {
        const ul = $("<ul></ul>", {
            id: `${this.element.attr("id")}_menu`,
            "class": "menuButtonMenu",
            role: "menu",
            tabindex: "-1",
            "aria-labelledby": this.element.attr("id")
        });
        this.onOpen = $.proxy(this.onOpen, this);
        this.onClose = $.proxy(this.onClose, this);
        this.popup = new kendo.ui.Popup(ul, {
            anchor: this.element,
            origin: _origin(this.options.direction),
            position: _position(this.options.direction),
            open: this.onOpen,
            close: this.onClose
        });
        this._popupEvents();
    },

    onOpen() {
        this.element.attr("aria-expanded", "true");
        this.current(this.items().first());
    },

    onClose() {
        this.element.removeAttr("aria-expanded");
        this.popup.element.removeAttr("aria-activedescendant");
        this.items().removeClass("k-state-focused");
    },

    _popupEvents() {
        this.popup.element.on("click", ">*", (e) => {
            this.select(e.currentTarget);
        }).on("keydown", (e) => {
            if(e.keyCode === kendo.keys.DOWN) {
                this.navigate();
                e.preventDefault();
            } else if(e.keyCode === kendo.keys.UP) {
                this.navigate(false);
                e.preventDefault();
            } else if(e.keyCode === kendo.keys.ENTER || e.keyCode === kendo.keys.SPACEBAR) {
                this.select(this.current());
                e.preventDefault();
            } else if(e.keyCode === kendo.keys.ESC || e.keyCode === kendo.keys.TAB) {
                this.popup.close();
            }
        });
    },

    items: function() {
        return this.popup.element.children();
    },

    dataItem(selector) {
        return this.dataSource.getByUid($(selector).attr("data-uid"));
    },

    setDataSource: function(ds) {
        if(this.dataSource) {
            this.dataSource.unbind("change", this.refresh);
        }
        this.dataSource = kendo.data.DataSource.create(ds);
        this.dataSource.bind("change", this.refresh);
        if(this.options.autoBind) {
            this.dataSource.fetch();
        }
    },

    refresh: function() {
        this.trigger("dataBinding");
        this.popup.element.empty();
        var items = this.dataSource.view();
        items.forEach((item) => {
            const render = $(this._renderItem(item));
            render
                .attr("id", kendo.guid())
                .attr("role", "menuitem")
                .attr("data-uid", item.uid)
                .addClass("menuButtonMenuItem");
            this.popup.element.append(render);
        });
        this.trigger("dataBound");
    },

    select(item) {
        this.popup.close();
        this.trigger("select", this.dataItem(item));
    },

    current(item) {
        if(!arguments.length) {
            const descendant = this.popup.element.attr("aria-activedescendant");
            if(descendant) {
                return this.items().filter(`#${descendant}`);
            }
            return $();
        }
        this.popup.element.attr("aria-activedescendant", item.attr("id"));
    },

    navigate(isNext = true) {
        const current = this.current();
        let item;
        if(isNext) {
            item = current.next();
            if(!item.length) {
                item = this.items().first();
            }
        } else {
            item = current.prev();
            if(!item.length) {
                item = this.items().last();
            }
        }
        this.current(item);
        this.visual();
    },

    visual() {
        this.items().removeClass("k-state-focused");
        this.current().addClass("k-state-focused");
    },

    /**
     * Override
     */
    focus() {
        if(this.popup.visible()) {
            this.popup.element.focus();
            this.visual();
            return;
        }
        DFButton.fn.focus.apply(this, arguments);
    },

    /**
     * Override
     * Combine promises instead of taking the last one
     */
    handleValuePromise(val) {
        if(this._valuePromise) {
            val = $.when(val, this._valuePromise);
        }
        DFButton.fn.handleValuePromise.call(this, val);
    },

    /**
     * Override
     * Allow click when menu is loading
     */
    _click() {
        if(this.options.openWhenBusy) {
            return kendo.ui.Button.fn._click.apply(this, arguments);
        }
        DFButton.fn._click.apply(this, arguments);
    },

    /**
     * Override
     * Set focus to popup when popup activates from key nav
     */
    _keydown(e) {
        DFButton.fn._keydown.apply(this, arguments);
        let focusAfterActivate = false;
        let open = false;
        if(e.keyCode === kendo.keys.ENTER || e.keyCode === kendo.keys.SPACEBAR) {
            focusAfterActivate = true;
        } else if(e.keyCode === kendo.keys.DOWN) {
            focusAfterActivate = true;
            if(this.popup.visible()) {
                this.focus();
            } else {
                open = true;
            }
        }
        if(focusAfterActivate && !this.popup.visible()) {
            this.popup.one("activate", () => {
                this.focus();
            });
            if(open) {
                this.popup.open();
            }
        }
    }
});

kendo.ui.plugin(MenuButton);
export default MenuButton;
